/**
 * CardTooltip — hover detail panel. Anchored next to the source card,
 * fades in after 250ms hover (snappier than the prior 600ms — testers
 * found the long delay frustrating).
 *
 * Shows:
 *   - Large image preview (full-art reference next to the smaller
 *     in-play card)
 *   - Header line: cost · power · type · orbital · rarity (only the
 *     fields the cards endpoint provides; older snapshots may render
 *     a thinner header)
 *   - Keyword pills (clicking the card itself opens the click-to-zoom
 *     overlay for an even larger view)
 *   - Rules text with §7 keyword highlighting
 *   - Set-id and eval-status footnote
 *
 * Positioning: prefer to the right of the card; flip to the left when
 * the panel would overflow the viewport. Vertically clamp inside the
 * window so a card near the bottom doesn't push the panel off-screen.
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CardData } from '../types.js';
import { KeywordSpan } from './KeywordTooltip.js';

type Props = {
  card: CardData;
  imageUrl?: string;
  anchorRect: DOMRect;
};

const HOVER_DELAY_MS = 250;
// Sized so a 2:3 portrait card image renders at its full aspect ratio
// (~320×480) on the left rail with the text body fitting alongside.
const PANEL_WIDTH = 640;
const PANEL_MAX_HEIGHT = 540;
const IMAGE_RAIL_WIDTH = 320;

export function CardTooltip({ card, imageUrl, anchorRect }: Props) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), HOVER_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  // Prefer right; flip to left when right edge would overflow. Clamp
  // vertically so the panel always fits within the window.
  const flipLeft = anchorRect.right + PANEL_WIDTH + 16 > window.innerWidth;
  const left = flipLeft ? Math.max(8, anchorRect.left - PANEL_WIDTH - 8) : anchorRect.right + 8;
  const rawTop = anchorRect.top - 20;
  const top = Math.min(Math.max(8, rawTop), Math.max(8, window.innerHeight - PANEL_MAX_HEIGHT - 8));

  const keywords = (card.keywords ?? []).filter((k) => k.length > 0);
  const cardType = card.card_type;
  const orbital = card.orbital;
  const cost = card.cost;
  const power = card.power;
  const rarity = card.rarity;

  const panel = (
    <div
      style={{
        position: 'fixed',
        left,
        top,
        width: PANEL_WIDTH,
        maxHeight: PANEL_MAX_HEIGHT,
        zIndex: 60,
      }}
      className="pointer-events-none flex overflow-hidden rounded-card bg-cosmos-900 shadow-2xl ring-1 ring-cosmos-700"
    >
      {/* Left rail — full card image at its native 2:3 aspect ratio.
          object-contain (not cover) so we see the entire art rather
          than a crop. */}
      {imageUrl && (
        <div
          className="flex flex-shrink-0 items-start justify-center bg-cosmos-950 p-2"
          style={{ width: IMAGE_RAIL_WIDTH }}
        >
          <img
            src={imageUrl}
            alt={card.name}
            className="h-auto w-full rounded-card object-contain ring-1 ring-cosmos-700"
            draggable={false}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      )}
      {/* Right body — name, stats, keywords, text. */}
      <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-3">
        <h4 className="font-display text-lg leading-tight text-cosmos-50">{card.name}</h4>
        <div className="flex flex-wrap gap-1.5 text-[10px]">
          {cardType && <Chip label={cardType} tone="amber" />}
          {orbital && <Chip label={orbital} tone="cosmos" />}
          {typeof cost === 'number' && <Chip label={`Cost ${cost}`} tone="violet" />}
          {typeof power === 'number' && <Chip label={`Pow ${power}`} tone="rose" />}
          {rarity && <Chip label={rarity} tone="cosmos" />}
        </div>
        {keywords.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {keywords.map((k) => (
              <span
                key={k}
                className="rounded bg-cosmos-800 px-1.5 py-0.5 font-mono text-[10px] text-cosmos-200 ring-1 ring-cosmos-700"
              >
                {k}
              </span>
            ))}
          </div>
        )}
        <p className="whitespace-pre-line text-[13px] text-cosmos-100">
          <RenderWithKeywords text={card.raw_text || '(no rules text)'} />
        </p>
        <div className="mt-auto flex gap-2 border-cosmos-800 border-t pt-2 text-[10px] text-cosmos-500">
          <span>{card.set_id}</span>
          <span>·</span>
          <span>{card.eval_status}</span>
          <span className="ml-auto text-cosmos-600">click to enlarge</span>
        </div>
      </div>
    </div>
  );

  // Portal into body so the panel escapes any clipping/overflow context
  // of the source card's parents (realm tile overflow, hand strip, etc.).
  return createPortal(panel, document.body);
}

function Chip({ label, tone }: { label: string; tone: 'amber' | 'rose' | 'violet' | 'cosmos' }) {
  const cls =
    tone === 'amber'
      ? 'bg-amber-900/60 text-amber-200 ring-amber-700/60'
      : tone === 'rose'
        ? 'bg-rose-900/60 text-rose-200 ring-rose-700/60'
        : tone === 'violet'
          ? 'bg-violet-900/60 text-violet-200 ring-violet-700/60'
          : 'bg-cosmos-800/80 text-cosmos-200 ring-cosmos-700';
  return (
    <span className={`rounded px-1.5 py-0.5 font-mono uppercase tracking-wide ring-1 ${cls}`}>
      {label}
    </span>
  );
}

/** Walks the card text and wraps any §7 keyword in a KeywordSpan tooltip trigger. */
function RenderWithKeywords({ text }: { text: string }) {
  const parts = text.split(/(\s+|[.,;:!?()])/);
  return (
    <>
      {parts.map((part, i) => {
        const word = part.trim();
        if (!word)
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: text is split into N positional fragments; index is the only stable identity.
            <span key={`ws-${i}`}>{part}</span>
          );
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: text is split into N positional fragments; index is the only stable identity.
          <KeywordSpan key={`kw-${i}`} word={word} fallback={part} />
        );
      })}
    </>
  );
}
